import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    ExistingProvider,
    forwardRef,
    HostBinding,
    Input,
    OnChanges,
    ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {IMaskDirective} from 'angular-imask';
import IMask from 'imask';
import moment from 'moment';
import {TypedSimpleChanges} from '../../common/typed-on-changes';
import {ControlValueAccessorWrapperBaseComponent} from '../control-value-accessor-wrapper-base.component';
import {Calendar} from './calendar';

const DATE_PICKER_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DatePickerComponent),
    multi: true
};

const IS_SELECT_DISABLED_CLASS = '-disabled';

interface CalendarDay {
    value: Date;
    styleClass: string;
    isEnabled: boolean;
    displayValue: number;
}

const isInvalidDate = (date: Date) => isNaN(date as undefined as number);

const setDateTimePart = (date: Date, dateTimePart: Date, dateTimePartUnit: moment.unitOfTime.StartOf) =>
    moment(date).startOf(dateTimePartUnit).add(
        dateTimePart.getTime() - moment(dateTimePart).startOf(dateTimePartUnit).valueOf()
    ).toDate();

const setDayTime = (date: Date, dayTimeDate: Date) =>
    setDateTimePart(date, dayTimeDate, 'day');

const IMASK_MOMENT_FORMAT_BLOCKS = {
    YYYY: {
        mask: IMask.MaskedRange,
        from: 1900,
        to: 2999,
        placeholderChar: 'Y',
        maxLength: 4
    },
    MM: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 12,
        placeholderChar: 'M',
        maxLength: 2
    },
    DD: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 31,
        placeholderChar: 'D',
        maxLength: 2
    },
    HH: {
        mask: IMask.MaskedRange,
        from: 0,
        to: 23,
        placeholderChar: 'H',
        maxLength: 2
    },
    mm: {
        mask: IMask.MaskedRange,
        from: 0,
        to: 59,
        placeholderChar: 'm',
        maxLength: 2
    },
    ss: {
        mask: IMask.MaskedRange,
        from: 0,
        to: 59,
        placeholderChar: 's',
        maxLength: 2
    }
};

/**
 * Input with dropdown calendar.
 *
 * Usage:
 * <date-picker
 *   [(ngModel)]="date"
 *   [dateFormat]="dateFormat"
 * </date-picker>
 */
@Component({
    selector: 'date-picker',
    styleUrls: ['./date-picker.component.scss'],
    templateUrl: './date-picker.component.html',
    providers: [DATE_PICKER_CONTROL_VALUE_ACCESSOR],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatePickerComponent
    extends ControlValueAccessorWrapperBaseComponent<Date>
    implements ControlValueAccessor, OnChanges, AfterViewInit {

    // api bindings
    @Input() public dateFormat: string;
    @Input() public placeholder: string;
    @Input() public rangeStart: Date;
    @Input() public rangeEnd: Date;
    @Input() public defaultDayTime: Date;
    @Input() public preserveDayTime: Date;
    @Input() public saveHoursAndLess: boolean = false;
    @Input() public saveSecondsAndLess: boolean = false;

    // time
    @Input() public calendarDays: CalendarDay[];
    @Input() public currentMonth: string;
    @Input() public dayNames: string[];

    public calendar: Calendar;
    public currentMonthNumber: number;
    public currentYear: number;
    public months: string[];

    // animation
    public animate: string;

    @HostBinding(`class.${IS_SELECT_DISABLED_CLASS}`)
    @HostBinding('attr.disabled')
    public isDisabled: boolean = null; // Null is required in order to remove "disabled" attribute.

    @Input()
    public disableFutureDates: boolean = false;

    public isCalendarVisible: boolean = false;

    @ViewChild(IMaskDirective, {static: true})
    public override innerValueAccessor: IMaskDirective<IMask.MaskedDateOptions>;

    public maskOptions: IMask.MaskedDateOptions;

    private isViewInitialized: boolean = false;

    constructor(
        public elementRef: ElementRef,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        super();

        this.dateFormat = 'YYYY-MM-DD';
        // time
        this.calendar = new Calendar();
        this.dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        this.months = [
            'January', 'February', 'March', 'April', 'May', 'June', 'July',
            'August', 'September', 'October', 'November', ' December'
        ];

        this.createMaskOptions();
        this.setCalendar(new Date());
    }

    public override writeValue(value: Date): void {
        super.writeValue(value);

        this.setCalendar(value);
    }

    public override setDisabledState(isDisabled: boolean): void {
        super.setDisabledState(isDisabled);

        this.isDisabled = isDisabled || null; // Null is required in order to remove "disabled" attribute.
    }

    public ngOnChanges(changes: TypedSimpleChanges<DatePickerComponent>): void {
        if (changes.dateFormat) {
            this.createMaskOptions();
        }

        if (this.calendarDays) {
            if (changes.rangeStart || changes.rangeEnd || changes.disableFutureDates) {
                this.setCalendarDaysArray();
            }
        }
    }

    public ngAfterViewInit(): void {

        /**
         * A workaround for angular-imask directive:
         * this directive initializes imask library in ngAfterViewInit,
         * that's why we shouldn't call it's writeValue earlier.
         * Also we need setTimeout since we can't do changes in DOM in ngAfterViewInit.
         */
        this.isViewInitialized = true;
        setTimeout(
            () => this.initInnerValueAccessor(),
            0
        );
    }

    // Click Handlers
    // ------------------------------------------------------------------------------------
    public onArrowLeftClick(): void {
        const currentMonth: number = this.currentMonthNumber;
        let newYear: number = this.currentYear;
        let newMonth: number;

        if (currentMonth === 0) {
            newYear = this.currentYear - 1;
            newMonth = 11;
        } else {
            newMonth = currentMonth - 1;
        }

        const newDate = moment(new Date(newYear, newMonth))
            .endOf('month').toDate();

        if (!this.rangeStart || newDate.getTime() >= this.rangeStart.getTime()) {
            this.currentYear = newYear;
            this.currentMonthNumber = newMonth;
            this.setCurrentMonth(newMonth);
            this.triggerAnimation('left');
        }
    }

    public onArrowRightClick(): void {
        const currentMonth: number = this.currentMonthNumber;
        let newYear: number = this.currentYear;
        let newMonth: number;

        if (currentMonth === 11) {
            newYear = this.currentYear + 1;
            newMonth = 0;
        } else {
            newMonth = currentMonth + 1;
        }

        const newDate = new Date(newYear, newMonth);
        if (!this.rangeEnd || newDate.getTime() <= this.rangeEnd.getTime()) {
            this.currentYear = newYear;
            this.currentMonthNumber = newMonth;
            this.setCurrentMonth(newMonth);
            this.triggerAnimation('right');
        }
    }

    public toggleCalendar(state?: boolean): void {
        state = state !== undefined
            ? state
            : !this.isCalendarVisible;

        if (state) {
            this.setCalendarDaysArray();
        }

        this.isCalendarVisible = state;
        this.changeDetectorRef.markForCheck();
    }

    public onSelectDay(day: CalendarDay): void {
        if (day.isEnabled) {
            this.selectDay(day.value);
            this.toggleCalendar(false);
        }
    }

    // Helpers
    // ------------------------------------------------------------------------------------
    public isChosenDay(day: Date): boolean {
        return day && this.value && this.getDateValue(day) === this.getDateValue(this.value);
    }

    public isCurrentDay(day: Date): boolean {
        const getCurrentDateValue = () => moment().startOf('day').valueOf();

        return day && this.getDateValue(day) === getCurrentDateValue();
    }

    public isDisabledDay(day: Date): boolean {
        const mDate = moment(this.keepDayTime(day));

        const isBeforeRange = () =>
            this.rangeStart && mDate.isBefore(this.rangeStart);

        const isAfterRange = () =>
            this.rangeEnd && mDate.isAfter(this.rangeEnd);

        return !day
            || this.disableFutureDates && mDate.isAfter(moment())
            || isBeforeRange()
            || isAfterRange();
    }

    public getClassForDay(day: Date): string {
        const dict: Record<string, () => boolean> = {
            'day-is-empty': () => !day,
            'current-day': () => day && this.isCurrentDay(day),
            'chosen-day': () => day && this.isChosenDay(day),
            'disabled-day': () => day && this.isDisabledDay(day)
        };

        return Object.keys(dict)
            .filter(key => dict[key]())
            .join(' ');
    }

    public getDateValue(date: Date): number {
        return moment(date).startOf('day').valueOf();
    }

    public triggerAnimation(direction: string): void {
        this.animate = direction;
        setTimeout(() => this.animate = 'reset', 185);
    }

    protected override initInnerValueAccessor(): void {

        /**
         * A workaround for angular-imask directive:
         * this directive initializes imask library in ngAfterViewInit,
         * that's why we shouldn't call it's writeValue earlier.
         */
        if (this.isViewInitialized) {
            super.initInnerValueAccessor();
        }
    }

    protected override isOuterValuesEqual(date1: Date, date2: Date): boolean {
        return !!date1 === !!date2
            && (!date1 || (
                date1.valueOf() === date2.valueOf()
            ));
    }

    protected override innerValueToOuterValue(innerValue: Date): Date {
        return this.safeKeepDayTime(innerValue);
    }

    private keepDayTime(origDate: Date): Date {
        let date = origDate;

        if (origDate) {
            if (this.preserveDayTime) {
                date = setDayTime(origDate, this.preserveDayTime);
            } else if (this.value) {
                date = setDayTime(origDate, this.value);
            } else if (this.defaultDayTime) {
                date = setDayTime(origDate, this.defaultDayTime);
            }
        }

        return date;
    }

    private safeKeepDayTime(origDate: Date): Date {
        let date = origDate;

        if (origDate) {
            if (this.preserveDayTime) {
                date = setDayTime(origDate, this.preserveDayTime);
            } else if (this.value) {
                if (this.saveHoursAndLess) {
                    date = setDayTime(origDate, this.value);
                } else if (this.saveSecondsAndLess) {
                    date = setDateTimePart(origDate, this.value, 'minute');
                }
            }
        }

        return date;
    }

    private setCalendar(date: Date): void {
        if (date && !isInvalidDate(date)) {
            this.currentMonthNumber = date.getMonth();
            this.currentMonth = this.months[this.currentMonthNumber];
            this.currentYear = date.getFullYear();

            this.setCalendarDaysArray();
        }
    }

    private setCurrentMonth(monthNumber: number): void {
        this.currentMonth = this.months[monthNumber];
        this.setCalendarDaysArray();
    }

    private setCalendarDaysArray(): void {
        const calendarArray = this.calendar.monthDays(this.currentYear, this.currentMonthNumber);
        const calendarDays = [].concat(...calendarArray);

        this.calendarDays = calendarDays.map(day => ({
            value:        day,
            styleClass:   this.getClassForDay(day),
            isEnabled:    !this.isDisabledDay(day),
            displayValue: day && day.getDate()
        }));
    }

    private selectDay(day: Date): void {
        let date = moment(day).startOf('day').toDate();

        date = this.keepDayTime(date);

        const isChanged = !this.isOuterValuesEqual(this.value, date);

        super.writeValue(date);
        this.toggleCalendar(false);

        if (isChanged) {
            this.propagateOnChange(date);
        }

        this.setCalendarDaysArray();
    }

    private createMaskOptions(): void {
        this.maskOptions = {
            mask: Date,
            pattern: this.dateFormat,
            lazy: true, // lazy=false makes placeholder always visible
            overwrite: false, // true enables characters overwriting instead of inserting
            autofix: false,
            format: date => moment(date).format(this.dateFormat),
            parse: str => moment(str, this.dateFormat).toDate(),
            blocks: IMASK_MOMENT_FORMAT_BLOCKS as any
        };
    }

}
